import { useState, useEffect, useRef } from 'react';
import CreativeExplore from '../../../services/creativeExplore';
import { getCreativesDefaultParams } from '../../creativesQueryUtils';
import { Dimensions } from '../../consts';

const CreativeExploreAPI = new CreativeExplore();

const flattenCohortData = (row, cohortMetrics, cohortPeriods) => {
    const flattenedRow = { ...row };
    cohortMetrics.forEach(metric => {
        cohortPeriods.forEach(period => {
            const flatKey = `${metric}_${period}`;
            if (row[metric] && row[metric][period] !== undefined) {
                flattenedRow[flatKey] = row[metric][period];
            }
        });
    });
    return flattenedRow;
};

export const useCreativePerformanceData = ({
    app,
    source,
    imageHashes = [],
    dimensions = [],
    aiDimensions = [],
    metrics = [],
    cohortFields = [],
    dateRange,
    timeBreakdown = 'day',
    shouldSkip = false,
    additionalFilters = [],
}) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [metadata, setMetadata] = useState({});
    const [error, setError] = useState(null);
    const dimensionsKey = JSON.stringify(dimensions.concat(aiDimensions));
    const metricsKey = JSON.stringify(metrics);
    const cohortFieldsKey = JSON.stringify(cohortFields);
    const imageHashesKey = JSON.stringify(imageHashes);
    const additionalFiltersKey = JSON.stringify(additionalFilters);

    useEffect(() => {
        if (shouldSkip) {
            setLoading(false);
            return;
        }

        if (!app || !source || !imageHashes.length || !metrics.length || !dateRange) {
            setData([]);
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);
        setData([]);

        const baseParams = getCreativesDefaultParams(dateRange, app, source, metrics, cohortFields, null);

        const additionalApiFilters = (additionalFilters || [])
            .filter(({ values }) => values?.length)
            .map(({ dimension, values }) => ({
                dimension,
                operator: 'in',
                values,
                options: { no_lowercase: true },
            }));

        const queryParams = {
            ...baseParams,
            dimensions,
            technical_specs_dimensions: [Dimensions.CREATIVE_IMAGE_HASH],
            creatives_ai_dimensions: aiDimensions,
            time_breakdown: [timeBreakdown],
            filters: [
                { dimension: 'app', operator: 'in', values: [app], options: { no_lowercase: true } },
                { dimension: 'source', operator: 'in', values: [source], options: { no_lowercase: true } },
                ...additionalApiFilters,
            ],
            post_process_filters: [
                {
                    field: Dimensions.CREATIVE_IMAGE_HASH,
                    operator: 'in',
                    value: imageHashes,
                    valueTo: null,
                },
            ],
        };

        CreativeExploreAPI.runQuery(queryParams, {
            unique: true,
            suffix: `${dimensionsKey}_${imageHashesKey}_${metricsKey}_${cohortFieldsKey}_${timeBreakdown}_${additionalFiltersKey}`,
        })
            .then(response => {
                if (cancelled) return;

                const results = (response.results || []).map(row =>
                    flattenCohortData(row, baseParams.cohort_metrics, baseParams.cohort_periods)
                );
                setData(results);
                setMetadata(response.metadata || {});
            })
            .catch(err => {
                if (cancelled) return;
                console.error('Error fetching creative performance data:', err);
                setError(err);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [
        shouldSkip,
        app,
        source,
        imageHashesKey,
        dimensionsKey,
        metricsKey,
        cohortFieldsKey,
        additionalFiltersKey,
        timeBreakdown,
        dateRange.startDate,
        dateRange.endDate,
    ]);

    return { loading, data, metadata, error };
};

/**
  Checks if the fetched data belongs to the requested breakdown.
  This validation is necessary because React effects run after render. When switching breakdowns,
  there's a render cycle where `selectedBreakdown` has changed but `data` still contains
  results from the previous breakdown (the effect hasn't run yet to fetch new data).
 */
const dataMatchesBreakdown = (data, breakdownValue) => {
    if (!data?.length) return false;
    return breakdownValue in data[0];
};

export const useBreakdownChartData = ({
    app,
    source,
    imageHashes,
    metrics,
    cohortFields,
    dateRange,
    selectedBreakdown,
    isAiDimension = false,
    additionalDimensions = [],
    additionalFilters = [],
}) => {
    const cacheRef = useRef({});
    const imageHashesKey = JSON.stringify(imageHashes);

    useEffect(() => {
        cacheRef.current = {};
    }, [imageHashesKey]);

    const cachedData = cacheRef.current[selectedBreakdown?.value]?.data;
    const cachedMetadata = cacheRef.current[selectedBreakdown?.value]?.metadata;

    const baseDimensions = isAiDimension ? [] : [selectedBreakdown?.value].filter(Boolean);

    const { loading, data, metadata, error } = useCreativePerformanceData({
        app,
        source,
        imageHashes,
        dimensions: [...baseDimensions, ...additionalDimensions],
        aiDimensions: isAiDimension ? [selectedBreakdown?.value].filter(Boolean) : [],
        metrics,
        cohortFields,
        dateRange,
        timeBreakdown: 'all',
        shouldSkip: !selectedBreakdown || !!cachedData,
        additionalFilters,
    });

    const hasValidData = !loading && dataMatchesBreakdown(data, selectedBreakdown?.value) && !error;

    useEffect(() => {
        if (hasValidData && !cachedData && selectedBreakdown?.value) {
            cacheRef.current[selectedBreakdown.value] = { data, metadata };
        }
    }, [data, metadata, hasValidData, selectedBreakdown?.value, cachedData]);

    const chartData = cachedData || (hasValidData ? data : []);
    const chartMetadata = cachedMetadata || (hasValidData ? metadata : {});

    return {
        data: chartData,
        metadata: chartMetadata,
        loading: !selectedBreakdown || loading,
    };
};
